import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  Icon,
  Badge,
  Spinner,
  SimpleGrid,
  Heading,
  useColorModeValue,
  Card,
  CardBody,
  Button,
  Link,
  Image,
  Skeleton,
  Avatar,
  Tooltip,
  Progress,
} from '@chakra-ui/react';
import { 
  FaFrown, 
  FaBookOpen, 
  FaExternalLinkAlt, 
  FaCheckCircle, 
  FaBookmark,
  FaStar,
  FaGlobe,
  FaNewspaper,
  FaBook,
  FaChevronRight,
  FaTrophy,
  FaMedal,
  FaAward,
  FaSearch,
  FaChartLine,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getYearData } from '../services/yearService';
import { getReviews } from '../services/reviewService';
import { getReadingItems } from '../services/playbookService';
import { getBusinessImages } from '../services/imageService';
import { logger } from '../utils/logger';
import {
  MarketMomentumMap,
  MarketHeatGauge,
} from './MarketDataVisualizations';
import {
  calculateSeoScore,
  getSeoScoreColor,
  getTrafficValue,
  sortBusinessesBySeoScore,
} from '../utils/competitiveIntelHelpers';
import BusinessReviewModal from './BusinessReviewModal';

// Source icon mapping for reading items
const getSourceIcon = (source) => {
  const sourceLower = source?.toLowerCase() || '';
  if (sourceLower.includes('forbes')) return FaNewspaper;
  if (sourceLower.includes('inc')) return FaNewspaper;
  if (sourceLower.includes('harvard')) return FaBook;
  if (sourceLower.includes('entrepreneur')) return FaNewspaper;
  return FaGlobe;
};

/**
 * SEO Score Widget - Displays market SEO health metrics
 * Shows top performers, average score, and SEO distribution
 */
const SeoScoreWidget = ({ reviewsData, businessImages, yearData }) => {
  const navigate = useNavigate();
  
  // State for BusinessReviewModal
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Calculate SEO scores for all businesses
  const seoData = useMemo(() => {
    if (!reviewsData || reviewsData.length === 0) {
      return { topPerformers: [], avgScore: 0, totalWithData: 0, distribution: {} };
    }

    const scored = reviewsData
      .filter(business => business.traffic_data?.metrics?.estimated_monthly_visitors > 0)
      .map(business => ({
        ...business,
        seoScore: calculateSeoScore(business),
      }))
      .sort((a, b) => b.seoScore - a.seoScore);

    const totalWithData = scored.length;
    const avgScore = totalWithData > 0 
      ? scored.reduce((sum, b) => sum + b.seoScore, 0) / totalWithData 
      : 0;

    // Calculate distribution
    const distribution = {
      excellent: scored.filter(b => b.seoScore >= 100).length,
      good: scored.filter(b => b.seoScore >= 50 && b.seoScore < 100).length,
      moderate: scored.filter(b => b.seoScore >= 20 && b.seoScore < 50).length,
      low: scored.filter(b => b.seoScore < 20).length,
    };

    return {
      topPerformers: scored.slice(0, 5),
      avgScore,
      totalWithData,
      distribution,
      maxScore: scored[0]?.seoScore || 0,
    };
  }, [reviewsData]);

  // Combine data for BusinessReviewModal
  const combinedData = useMemo(() => {
    if (!reviewsData || !yearData) return reviewsData || [];
    return reviewsData.map((currentData) => {
      const historicalData = yearData.find(
        (yd) => yd.maps_uri === currentData.maps_uri
      );
      return {
        ...currentData,
        ...(historicalData?.data || {}),
        data: historicalData?.data || null,
        historicalData: historicalData?.data || null,
      };
    });
  }, [reviewsData, yearData]);

  // Sorted arrays for BusinessReviewModal
  const sortedByTotal = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort((a, b) => (b.rating_count || 0) - (a.rating_count || 0));
  }, [combinedData]);

  const sortedBySentiment = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort(
      (a, b) => (b.sentimentScore || b.avg_rating || 0) - (a.sentimentScore || a.avg_rating || 0)
    );
  }, [combinedData]);

  const sortedByGrowth = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData].sort(
      (a, b) => (b.totalGrowth || 0) - (a.totalGrowth || 0)
    );
  }, [combinedData]);

  const sortedByTraffic = useMemo(() => {
    if (!combinedData.length) return [];
    return [...combinedData]
      .map((business) => ({
        ...business,
        trafficValue: getTrafficValue(business),
      }))
      .filter((business) => (business.trafficValue || 0) > 0)
      .sort((a, b) => (b.trafficValue || 0) - (a.trafficValue || 0));
  }, [combinedData]);

  const sortedBySeo = useMemo(
    () => sortBusinessesBySeoScore(combinedData),
    [combinedData]
  );

  // Handler for clicking on a business to open BusinessReviewModal
  const handleBusinessClick = useCallback((business) => {
    if (!business) return;

    const businessMapsUri = business.maps_uri || business.mapsUri;
    const businessName = business.business_name || business.name;
    const businessData =
      combinedData.find(b => businessMapsUri && b.maps_uri === businessMapsUri) ||
      combinedData.find(b => businessName && b.name === businessName) ||
      business;
    if (businessData) {
      setSelectedBusiness(businessData);
      setIsModalOpen(true);
    }
  }, [combinedData]);

  // Position icons and colors
  const positionConfig = [
    { icon: FaTrophy, color: 'teal.400', gradient: 'linear(to-br, teal.300, cyan.400)', label: 'SEO Champion' },
    { icon: FaMedal, color: 'gray.400', gradient: 'linear(to-br, gray.300, gray.500)', label: '#2' },
    { icon: FaAward, color: 'orange.400', gradient: 'linear(to-br, orange.300, orange.500)', label: '#3' },
    { icon: FaChartLine, color: 'blue.400', gradient: 'linear(to-br, blue.300, blue.500)', label: '#4' },
    { icon: FaChartLine, color: 'purple.400', gradient: 'linear(to-br, purple.300, purple.500)', label: '#5' },
  ];

  const formatScore = (score) => {
    if (score >= 1000) return `${(score / 1000).toFixed(1)}K`;
    return score.toFixed(1);
  };

  if (!reviewsData || seoData.totalWithData === 0) {
    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <HStack spacing={3} mb={4}>
          <Icon as={FaSearch} color="teal.500" boxSize={5} />
          <Heading size="md" color={textColor}>SEO Score</Heading>
        </HStack>
        <Flex justify="center" align="center" minH="150px">
          <VStack spacing={2}>
            <Icon as={FaGlobe} boxSize={10} color="gray.300" />
            <Text color={subtleTextColor}>No SEO data available</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      p={6}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
    >
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={3}>
          <Box
            bgGradient="linear(to-r, teal.500, cyan.500)"
            p={2}
            borderRadius="lg"
          >
            <Icon as={FaSearch} color="white" boxSize={5} />
          </Box>
          <VStack align="start" spacing={0}>
            <Heading size="md" color={textColor}>
              SEO Score
            </Heading>
            <Text fontSize="xs" color={subtleTextColor}>
              Search Engine Optimization Performance
            </Text>
          </VStack>
        </HStack>
        <Button
          size="sm"
          colorScheme="teal"
          variant="outline"
          rightIcon={<Icon as={FaChevronRight} />}
          onClick={() => navigate('/dashboards/web-traffic')}
        >
          Full SEO Dashboard
        </Button>
      </Flex>

      {/* Main Content Grid */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
        {/* Market Stats */}
        <VStack spacing={4} align="stretch">
          <Box bg={statBg} p={4} borderRadius="lg">
            <Text fontSize="xs" fontWeight="semibold" color={subtleTextColor} textTransform="uppercase" mb={2}>
              Market Average
            </Text>
            <HStack spacing={2} align="baseline">
              <Text fontSize="3xl" fontWeight="bold" color={getSeoScoreColor(seoData.avgScore)}>
                {formatScore(seoData.avgScore)}
              </Text>
              <Text fontSize="sm" color={subtleTextColor}>SEO Score</Text>
            </HStack>
          </Box>
          
          <SimpleGrid columns={2} spacing={3}>
            <Box bg={statBg} p={3} borderRadius="lg" textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color="teal.500">
                {seoData.totalWithData}
              </Text>
              <Text fontSize="xs" color={subtleTextColor}>Tracked</Text>
            </Box>
            <Box bg={statBg} p={3} borderRadius="lg" textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color="green.500">
                {seoData.distribution.excellent + seoData.distribution.good}
              </Text>
              <Text fontSize="xs" color={subtleTextColor}>Performing</Text>
            </Box>
          </SimpleGrid>

          {/* Distribution */}
          <Box>
            <Text fontSize="xs" fontWeight="semibold" color={subtleTextColor} textTransform="uppercase" mb={3}>
              Score Distribution
            </Text>
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <Box w={2} h={2} bg="green.500" borderRadius="full" />
                  <Text fontSize="xs" color={textColor}>Excellent (100+)</Text>
                </HStack>
                <Text fontSize="xs" fontWeight="bold">{seoData.distribution.excellent}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack>
                  <Box w={2} h={2} bg="blue.500" borderRadius="full" />
                  <Text fontSize="xs" color={textColor}>Good (50-100)</Text>
                </HStack>
                <Text fontSize="xs" fontWeight="bold">{seoData.distribution.good}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack>
                  <Box w={2} h={2} bg="orange.500" borderRadius="full" />
                  <Text fontSize="xs" color={textColor}>Moderate (20-50)</Text>
                </HStack>
                <Text fontSize="xs" fontWeight="bold">{seoData.distribution.moderate}</Text>
              </HStack>
              <HStack justify="space-between">
                <HStack>
                  <Box w={2} h={2} bg="gray.400" borderRadius="full" />
                  <Text fontSize="xs" color={textColor}>Low (&lt;20)</Text>
                </HStack>
                <Text fontSize="xs" fontWeight="bold">{seoData.distribution.low}</Text>
              </HStack>
            </VStack>
          </Box>
        </VStack>

        {/* Top Performers - Takes 2 columns */}
        <Box gridColumn={{ lg: 'span 2' }}>
          <Text fontSize="xs" fontWeight="semibold" color={subtleTextColor} textTransform="uppercase" mb={4}>
            Top SEO Performers
          </Text>
          <VStack spacing={3} align="stretch">
            {seoData.topPerformers.map((business, index) => {
              const config = positionConfig[index];
              const scorePercent = seoData.maxScore > 0 
                ? (business.seoScore / seoData.maxScore) * 100 
                : 0;
              const rank = business.query_ranking_info?.url_ranking;
              const traffic = business.traffic_data?.metrics?.estimated_monthly_visitors || 0;

              return (
                <Box
                  key={business.maps_uri || index}
                  p={4}
                  bg={index === 0 ? 'teal.50' : statBg}
                  borderRadius="lg"
                  borderWidth={index === 0 ? '2px' : '1px'}
                  borderColor={index === 0 ? 'teal.200' : borderColor}
                  transition="all 0.2s"
                  _hover={{ transform: 'translateX(4px)', bg: hoverBg }}
                  cursor="pointer"
                  onClick={() => handleBusinessClick(business)}
                >
                  <Flex align="center" gap={4}>
                    {/* Rank Badge */}
                    <Flex
                      w="36px"
                      h="36px"
                      borderRadius="full"
                      bgGradient={config.gradient}
                      justify="center"
                      align="center"
                      flexShrink={0}
                    >
                      {index < 3 ? (
                        <Icon as={config.icon} color="white" boxSize={4} />
                      ) : (
                        <Text color="white" fontWeight="bold" fontSize="sm">{index + 1}</Text>
                      )}
                    </Flex>

                    {/* Avatar */}
                    <Avatar
                      size="sm"
                      name={business.name}
                      src={businessImages?.[business.maps_uri] || ''}
                      bg="gray.200"
                    />

                    {/* Business Info */}
                    <Box flex={1} minW={0}>
                      <Tooltip label={business.name} placement="top" hasArrow>
                        <Text fontWeight="bold" fontSize="sm" noOfLines={1} color={textColor}>
                          {business.name}
                        </Text>
                      </Tooltip>
                      <HStack spacing={3} mt={1}>
                        <Text fontSize="xs" color={subtleTextColor}>
                          Rank: #{rank || '100+'}
                        </Text>
                        <Text fontSize="xs" color={subtleTextColor}>
                          {traffic.toLocaleString()} backlinks
                        </Text>
                      </HStack>
                    </Box>

                    {/* Score */}
                    <VStack spacing={0} align="end" flexShrink={0}>
                      <Text
                        fontWeight="extrabold"
                        fontSize="lg"
                        color={getSeoScoreColor(business.seoScore)}
                      >
                        {formatScore(business.seoScore)}
                      </Text>
                      <Text fontSize="2xs" color={subtleTextColor}>
                        SEO Score
                      </Text>
                    </VStack>
                  </Flex>

                  {/* Progress bar */}
                  <Box mt={3}>
                    <Progress
                      value={scorePercent}
                      size="xs"
                      colorScheme={index === 0 ? 'teal' : index === 1 ? 'gray' : 'orange'}
                      borderRadius="full"
                      bg="gray.100"
                    />
                  </Box>
                </Box>
              );
            })}
          </VStack>
        </Box>
      </SimpleGrid>

      {/* Formula Explanation */}
      <Box
        mt={6}
        p={4}
        bg={statBg}
        borderRadius="lg"
        borderLeftWidth="4px"
        borderLeftColor="teal.400"
      >
        <HStack spacing={2} mb={2}>
          <Icon as={FaChartLine} color="teal.500" boxSize={4} />
          <Text fontSize="sm" fontWeight="semibold" color={textColor}>
            How SEO Score is Calculated
          </Text>
        </HStack>
        <Text fontSize="xs" color={subtleTextColor}>
          <Text as="span" fontWeight="bold">SEO Score = Monthly Backlinks ÷ Search Rank.</Text>
          {' '}Higher scores indicate better visibility. Businesses with more backlinks relative 
          to their search ranking position perform better in organic search.
        </Text>
      </Box>

      {/* Business Review Modal */}
      <BusinessReviewModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        yearData={yearData || []}
        sortedByTotal={sortedByTotal}
        sortedBySentiment={sortedBySentiment}
        sortedByGrowth={sortedByGrowth}
        sortedByTraffic={sortedByTraffic}
        sortedBySeo={sortedBySeo}
      />
    </Box>
  );
};

// Relevance score colors
const getRelevanceColor = (score) => {
  if (score >= 4) return 'green';
  if (score >= 3) return 'yellow';
  return 'gray';
};

/**
 * ReadingCard - Individual reading item card component
 */
const ReadingCard = ({ item, onOpenLink, cardBg, borderColor, primaryColor, linkColor, subtleTextColor }) => {
  const isRead = !!item.read_at;
  const isSaved = item.user_feedback === 'saved';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const fallbackBg = useColorModeValue('blue.50', 'gray.700');
  const cardHoverBorder = useColorModeValue('blue.300', 'blue.500');

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={isRead ? borderColor : primaryColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.2s ease"
      _hover={{ 
        shadow: 'lg',
        transform: 'translateY(-2px)',
        borderColor: cardHoverBorder
      }}
      opacity={isRead ? 0.85 : 1}
      h="100%"
      display="flex"
      flexDirection="column"
      cursor="pointer"
      onClick={() => onOpenLink(item)}
    >
      {/* Preview Image */}
      {item.preview_image && !imageError ? (
        <Box
          position="relative"
          w="100%"
          h="120px"
          overflow="hidden"
          bg="gray.100"
        >
          {!imageLoaded && (
            <Skeleton
              position="absolute"
              top={0}
              left={0}
              w="100%"
              h="100%"
              startColor="gray.100"
              endColor="gray.300"
            />
          )}
          <Image
            src={item.preview_image}
            alt={item.title}
            w="100%"
            h="100%"
            objectFit="cover"
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            opacity={imageLoaded ? 1 : 0}
            transition="all 0.3s ease-in-out"
            _hover={{ transform: 'scale(1.05)' }}
          />
          {/* Source badge overlay */}
          <Badge 
            position="absolute" 
            top={2} 
            left={2} 
            colorScheme="blackAlpha" 
            fontSize="xs"
            bg="blackAlpha.700"
            color="white"
          >
            {item.source}
          </Badge>
          {/* Relevance score overlay */}
          <Badge 
            position="absolute" 
            top={2} 
            right={2} 
            colorScheme={getRelevanceColor(item.relevance_score)} 
            fontSize="xs"
          >
            <Flex align="center" gap={1}>
              <Icon as={FaStar} boxSize={2} />
              <Text as="span">{item.relevance_score}</Text>
            </Flex>
          </Badge>
        </Box>
      ) : (
        <Flex
          w="100%"
          h="80px"
          bg={fallbackBg}
          color={primaryColor}
          justify="center"
          align="center"
          position="relative"
        >
          <Icon as={getSourceIcon(item.source)} boxSize={8} opacity={0.7} />
          <Badge 
            position="absolute" 
            top={2} 
            left={2} 
            colorScheme="blackAlpha" 
            fontSize="xs"
            bg="blackAlpha.700"
            color="white"
          >
            {item.source}
          </Badge>
          <Badge 
            position="absolute" 
            top={2} 
            right={2} 
            colorScheme={getRelevanceColor(item.relevance_score)} 
            fontSize="xs"
          >
            <Flex align="center" gap={1}>
              <Icon as={FaStar} boxSize={2} />
              <Text as="span">{item.relevance_score}</Text>
            </Flex>
          </Badge>
        </Flex>
      )}

      <CardBody flex={1} display="flex" flexDirection="column" p={4}>
        {/* Title */}
        <Link
          color={linkColor}
          fontWeight="bold"
          fontSize="md"
          lineHeight="short"
          noOfLines={2}
          _hover={{ textDecoration: 'none', color: 'blue.600' }}
          mb={2}
          display="block"
        >
          {item.title}
        </Link>
        
        {/* Status badges */}
        <HStack spacing={2} flexWrap="wrap" mb={2}>
          {isRead && (
            <Badge colorScheme="green" variant="subtle" fontSize="xs" borderRadius="full">
              <Flex align="center" gap={1}>
                <Icon as={FaCheckCircle} boxSize={2} />
                <Text as="span">Read</Text>
              </Flex>
            </Badge>
          )}
          {isSaved && (
            <Badge colorScheme="purple" variant="subtle" fontSize="xs" borderRadius="full">
              <Flex align="center" gap={1}>
                <Icon as={FaBookmark} boxSize={2} />
                <Text as="span">Saved</Text>
              </Flex>
            </Badge>
          )}
        </HStack>

        {/* Relevance reason */}
        <Text color={subtleTextColor} fontSize="sm" noOfLines={2} flex={1}>
          {item.relevance_reason}
        </Text>

        {/* Footer with date */}
        <Box mt="auto" pt={3}>
          <HStack justify="space-between" align="center">
            <Text fontSize="xs" color={subtleTextColor}>
              {new Date(item.created_at).toLocaleDateString()}
            </Text>
            <Icon as={FaExternalLinkAlt} boxSize={3} color={subtleTextColor} />
          </HStack>
        </Box>
      </CardBody>
    </Card>
  );
};

/**
 * AnalyzeMonthSnapshot - Month in Review widget for the Analyze section
 * 
 * Displays:
 * - Market Demand Gauge (score 0-100)
 * - Market Momentum Map (bubble chart: domain strength vs traffic)
 * - Bad Reviews this Month (negative customer feedback)
 * - Reading List (curated industry articles with stats grid)
 */
const AnalyzeMonthSnapshot = () => {
  const [yearData, setYearData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);
  const [readingItems, setReadingItems] = useState([]);
  const [businessImages, setBusinessImages] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isReadingLoading, setIsReadingLoading] = useState(true);
  const navigate = useNavigate();

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const redBg = useColorModeValue('red.50', 'red.900');
  const primaryColor = useColorModeValue('blue.600', 'blue.400');
  const subtleTextColor = useColorModeValue('gray.500', 'gray.400');
  const linkColor = useColorModeValue('gray.900', 'white');
  const statCardBg = useColorModeValue('gray.50', 'gray.700');

  // Fetch year data and reviews data on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [yearResult, reviewsResult] = await Promise.all([
          getYearData(),
          getReviews(),
        ]);
        setYearData(yearResult);
        setReviewsData(reviewsResult || []);
        logger.log('AnalyzeMonthSnapshot - Loaded year data:', yearResult?.length, 'businesses');
        logger.log('AnalyzeMonthSnapshot - Loaded reviews data:', reviewsResult?.length, 'businesses');
        
        // Fetch business images (non-blocking)
        getBusinessImages().then(images => {
          if (images) {
            const imageMap = {};
            images.forEach(img => {
              if (img.maps_uri && img.high_res_image_url_fetched) {
                imageMap[img.maps_uri] = img.high_res_image_url_fetched;
              }
            });
            setBusinessImages(imageMap);
          }
        }).catch(err => {
          logger.log('Business images not available:', err.message);
        });
      } catch (error) {
        logger.error('Error fetching data for Analyze Month Snapshot:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch reading items separately (non-blocking)
  useEffect(() => {
    const fetchReadingData = async () => {
      setIsReadingLoading(true);
      try {
        const itemsData = await getReadingItems();
        // Filter to location scope and sort by relevance
        const sortedItems = (itemsData || [])
          .filter(item => item.scope === 'location')
          .sort((a, b) => {
            // Unread items first
            if (!a.read_at && b.read_at) return -1;
            if (a.read_at && !b.read_at) return 1;
            // Then by relevance score
            if (b.relevance_score !== a.relevance_score) {
              return b.relevance_score - a.relevance_score;
            }
            // Then by date
            return new Date(b.created_at) - new Date(a.created_at);
          })
          .slice(0, 6); // Show 6 items in grid
        setReadingItems(sortedItems);
        logger.log('AnalyzeMonthSnapshot - Loaded reading items:', sortedItems.length);
      } catch (error) {
        logger.error('Error fetching reading items for Analyze Month Snapshot:', error);
        setReadingItems([]);
      } finally {
        setIsReadingLoading(false);
      }
    };

    fetchReadingData();
  }, []);

  // Extract market_trends_insight for bad reviews
  const macroInsight = useMemo(() => {
    if (reviewsData && reviewsData.length > 0) {
      for (const business of reviewsData) {
        if (business.market_trends_insight) {
          return business.market_trends_insight;
        }
      }
    }

    if (yearData && yearData.length > 0) {
      for (const yd of yearData) {
        if (yd.market_trends_insight) {
          return yd.market_trends_insight;
        } else if (yd.data?.market_trends_insight) {
          return yd.data.market_trends_insight;
        }
      }
    }

    return null;
  }, [yearData, reviewsData]);

  // Parse bad reviews from market_trends_insight
  const badReviews = useMemo(() => {
    if (!macroInsight) return [];

    const quotes = [];
    const lines = macroInsight.split('\n');
    let currentGroup = null;
    let currentSection = null;

    for (const line of lines) {
      const trimmed = line.trim();
      const lowerLine = trimmed.toLowerCase();

      // Detect main sections
      if (lowerLine.includes('**market overview:**') || lowerLine.includes('1.')) {
        currentGroup = 'marketOverview';
        continue;
      } else if (lowerLine.includes('**strategies for success:**') || lowerLine.includes('2.')) {
        currentGroup = 'strategies';
        continue;
      }

      // Parse bad subsections
      if (currentGroup === 'marketOverview' && lowerLine.includes('**bad:**')) {
        currentSection = 'bad';
        continue;
      } else if (currentGroup === 'strategies' && lowerLine.includes('**not working:**')) {
        currentSection = 'notWorking';
        continue;
      } else if (lowerLine.includes('**good:**') || lowerLine.includes('**working:**') || lowerLine.includes('**changing:**')) {
        currentSection = null;
        continue;
      }

      // Parse quotes from bad/notWorking sections
      if ((currentSection === 'bad' || currentSection === 'notWorking') && lowerLine.includes('*data:*')) {
        const quotesContent = trimmed.replace(/.*\*data:\*/i, '').trim();
        if (quotesContent) {
          const quoteEntries = quotesContent.split(/,\s+(?=")/);
          quoteEntries.forEach((entry) => {
            const match = entry.match(/"([^"]+)"\s*-\s*([^(]+?)(?:\s*\(Review ID:.*?\))?\s*$/);
            if (match) {
              const quoteText = match[1].trim();
              const businessName = match[2].trim();
              if (quoteText && businessName) {
                quotes.push({ text: quoteText, business: businessName });
              }
            }
          });
        }
      }
    }

    // Remove duplicates
    const unique = quotes.filter((quote, index, self) => 
      index === self.findIndex(q => q.text === quote.text)
    );

    return unique.slice(0, 6); // Show up to 6 bad reviews
  }, [macroInsight]);

  // Calculate Market Momentum Map data (Gross Growth vs Gross Acceleration)
  const momentumMapData = useMemo(() => {
    if (!reviewsData || reviewsData.length === 0 || !yearData || yearData.length === 0) {
      return [];
    }

    // Get current month key for gross growth
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
    const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const prevMonthKey = `${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth() + 1).padStart(2, '0')}`;

    return reviewsData
      .map(business => {
        const yearBusiness = yearData.find(
          y => y.name === business.name || y.maps_uri === business.maps_uri
        );
        const monthlyData = yearBusiness?.data?.monthlyReviewCounts || {};
        const currentReviews = monthlyData[currentMonthKey] || 0;
        const prevReviews = monthlyData[prevMonthKey] || 0;
        const grossGrowth = currentReviews;
        const grossAcceleration = currentReviews - prevReviews;
        const totalReviews = business.rating_count || yearBusiness?.data?.totalReviews || 0;

        return {
          name: business.name,
          maps_uri: business.maps_uri,
          grossGrowth,
          grossAcceleration,
          totalReviews,
          isAscending: grossAcceleration > 0 && grossGrowth > 0,
        };
      })
      .filter(d => d.grossGrowth > 0 || d.grossAcceleration !== 0)
      .sort((a, b) => b.grossGrowth - a.grossGrowth)
      .slice(0, 20); // Top 20 businesses by gross growth
  }, [reviewsData, yearData]);

  // Calculate month-over-month comparison (last month vs the month before)
  const marketDemandScore = useMemo(() => {
    if (!yearData || yearData.length === 0) {
      return { score: 50, change: 0 };
    }

    // Get last month and the month before that
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthKey = `${lastMonth.getFullYear()}-${String(lastMonth.getMonth() + 1).padStart(2, '0')}`;
    const monthBeforeLast = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const monthBeforeLastKey = `${monthBeforeLast.getFullYear()}-${String(monthBeforeLast.getMonth() + 1).padStart(2, '0')}`;

    // Calculate total reviews for each month across all businesses
    let totalLastMonthReviews = 0;
    let totalMonthBeforeReviews = 0;

    yearData.forEach(business => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      totalLastMonthReviews += monthlyData[lastMonthKey] || 0;
      totalMonthBeforeReviews += monthlyData[monthBeforeLastKey] || 0;
    });

    // Calculate percentage change from month before to last month
    const percentChange = totalMonthBeforeReviews > 0 
      ? ((totalLastMonthReviews - totalMonthBeforeReviews) / totalMonthBeforeReviews) * 100 
      : 0;

    // Convert percentage change to a 0-100 gauge score
    // -50% or worse maps to 0, +50% or better maps to 100, 0% maps to 50
    const gaugeScore = Math.min(100, Math.max(0, 50 + percentChange));

    return { score: Math.round(gaugeScore), change: Math.round(percentChange) };
  }, [yearData]);

  // Reading stats
  const readingStats = useMemo(() => {
    return {
      total: readingItems.length,
      unread: readingItems.filter(item => !item.read_at).length,
      read: readingItems.filter(item => item.read_at).length,
    };
  }, [readingItems]);

  // Handle opening reading link
  const handleOpenReadingLink = useCallback((item) => {
    window.open(item.url, '_blank', 'noopener,noreferrer');
  }, []);

  if (isLoading) {
    return (
      <Box>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
          <Flex
            bg={cardBg}
            borderRadius="xl"
            borderWidth="1px"
            borderColor={borderColor}
            p={5}
            align="center"
            justify="center"
            minH="280px"
          >
            <Spinner size="lg" color="blue.500" />
          </Flex>
          <Box gridColumn={{ lg: 'span 2' }}>
            <Flex
              bg={cardBg}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
              p={5}
              align="center"
              justify="center"
              minH="280px"
            >
              <Spinner size="lg" color="blue.500" />
            </Flex>
          </Box>
        </SimpleGrid>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Row 1: Market Demand Gauge + Market Momentum Map */}
      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
        {/* Market Demand Gauge */}
        <MarketHeatGauge
          score={marketDemandScore.score}
          change={marketDemandScore.change}
          label="Market Demand"
        />

        {/* Market Momentum Map - Takes 2 columns */}
        <Box gridColumn={{ lg: 'span 2' }}>
          {momentumMapData.length > 0 ? (
            <MarketMomentumMap
              data={momentumMapData}
              height={280}
            />
          ) : (
            <Box
              bg={cardBg}
              borderRadius="xl"
              borderWidth="1px"
              borderColor={borderColor}
              p={6}
              h="280px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="gray.500">No competitive intel data available</Text>
            </Box>
          )}
        </Box>
      </SimpleGrid>

      {/* Row 2: SEO Score Widget - Full Width */}
      <SeoScoreWidget reviewsData={reviewsData} businessImages={businessImages} yearData={yearData} />

      {/* Row 3: Bad Reviews this Month */}
      {badReviews.length > 0 && (
        <Box
          bg={cardBg}
          p={6}
          borderRadius="xl"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <HStack spacing={3} mb={5}>
            <Icon as={FaFrown} color="red.500" boxSize={5} />
            <Heading size="md" color={textColor}>
              Bad Reviews this Month
            </Heading>
          </HStack>
          
          <VStack spacing={4} align="stretch">
            {badReviews.map((quote, idx) => (
              <Box
                key={idx}
                p={4}
                bg={redBg}
                borderRadius="md"
                borderLeftWidth="3px"
                borderLeftColor="red.400"
              >
                <Text fontSize="md" fontStyle="italic" color="gray.700" mb={2}>
                  "{quote.text}"
                </Text>
                <HStack spacing={2}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    — {quote.business}
                  </Text>
                  <Badge colorScheme="red" fontSize="xs">
                    Negative Review
                  </Badge>
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {/* Row 4: Reading List Section */}
      <Box
        bg={cardBg}
        p={6}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
      >
        {/* Header with stats */}
        <Flex 
          direction={{ base: 'column', md: 'row' }} 
          justify="space-between" 
          align={{ base: 'stretch', md: 'center' }}
          gap={4}
          mb={6}
        >
          <HStack spacing={3}>
            <Icon as={FaBookOpen} color={primaryColor} boxSize={6} />
            <Heading size="lg" color={textColor}>
              Reading List
            </Heading>
            {readingStats.unread > 0 && (
              <Badge colorScheme="blue" fontSize="sm" borderRadius="full" px={3}>
                {readingStats.unread} unread
              </Badge>
            )}
          </HStack>

          {/* Stats Row */}
          <HStack spacing={4}>
            <Box 
              bg={statCardBg} 
              px={4} 
              py={2} 
              borderRadius="lg"
              textAlign="center"
            >
              <Text fontSize="xl" fontWeight="bold" color={primaryColor}>{readingStats.total}</Text>
              <Text fontSize="xs" color={subtleTextColor}>Articles</Text>
            </Box>
            <Box 
              bg={statCardBg} 
              px={4} 
              py={2} 
              borderRadius="lg"
              textAlign="center"
            >
              <Text fontSize="xl" fontWeight="bold" color="blue.500">{readingStats.unread}</Text>
              <Text fontSize="xs" color={subtleTextColor}>Unread</Text>
            </Box>
            <Box 
              bg={statCardBg} 
              px={4} 
              py={2} 
              borderRadius="lg"
              textAlign="center"
            >
              <Text fontSize="xl" fontWeight="bold" color="green.500">{readingStats.read}</Text>
              <Text fontSize="xs" color={subtleTextColor}>Read</Text>
            </Box>
            <Button
              colorScheme="blue"
              variant="outline"
              size="sm"
              rightIcon={<Icon as={FaChevronRight} />}
              onClick={() => navigate('/playbook/reading')}
            >
              View All
            </Button>
          </HStack>
        </Flex>

        {/* Reading Items Grid */}
        {isReadingLoading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="lg" color={primaryColor} thickness="3px" />
          </Flex>
        ) : readingItems.length > 0 ? (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {readingItems.map(item => (
              <ReadingCard 
                key={item.reading_id} 
                item={item} 
                onOpenLink={handleOpenReadingLink}
                cardBg={cardBg}
                borderColor={borderColor}
                primaryColor={primaryColor}
                linkColor={linkColor}
                subtleTextColor={subtleTextColor}
              />
            ))}
          </SimpleGrid>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            p={8}
            bg={statCardBg}
            borderRadius="lg"
            minH="200px"
          >
            <Icon as={FaBookOpen} boxSize={12} color="gray.300" mb={4} />
            <Text color={subtleTextColor} fontSize="lg" mb={2}>No reading items available</Text>
            <Text color={subtleTextColor} fontSize="sm" textAlign="center">
              Industry articles will appear here as we find content relevant to your market.
            </Text>
          </Flex>
        )}

        {/* View More Link */}
        {readingItems.length > 0 && (
          <Flex justify="center" mt={6}>
            <Button
              colorScheme="blue"
              variant="ghost"
              size="lg"
              rightIcon={<Icon as={FaExternalLinkAlt} />}
              onClick={() => navigate('/playbook/reading')}
            >
              Explore Full Reading List
            </Button>
          </Flex>
        )}
      </Box>
    </VStack>
  );
};

export default AnalyzeMonthSnapshot;
